/**
 * O que faz: Contexto, calendário civil e chave de cache das métricas por identidade.
 * Onde usa: src/services/metricasService e src/hooks/useMetricas.
 * Conexões: Reaproveita serializarPeriodoDashboard para datas civis.
 * Tabelas: Nenhuma (cálculo em memória).
 * Data: 05/09/2026.
 */

import { serializarPeriodoDashboard } from '../dashboard/data/dashboardContracts';

export interface ContextoMetricas {
  usuarioId: string;
  clinicaId: string;
  modo: 'clinica' | 'admin';
  versaoPermissoes: string;
  periodo: { inicio: string; fim: string };
  comparar: boolean;
  filtros: Record<string, string | number | boolean | null>;
}

// Calendário civil em America/Sao_Paulo; sem DST desde 2019 o fuso é UTC-3 fixo.
// Usar 03:00Z mantém o dia escolhido mesmo com navegador em UTC.
const DESLOCAMENTO_SP_MINUTOS = 180;

function decomporDataCivil(texto: string): { ano: number; mes: number; dia: number } {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto);
  if (!partes) throw new Error('Selecione um período válido.');
  const ano = Number(partes[1]);
  const mes = Number(partes[2]);
  const dia = Number(partes[3]);
  // Fevereiro inválido e dias impossíveis falham aqui, sem virar resultado vazio.
  const prova = new Date(Date.UTC(ano, mes - 1, dia));
  if (prova.getUTCFullYear() !== ano || prova.getUTCMonth() !== mes - 1 || prova.getUTCDate() !== dia) {
    throw new Error('Selecione um período válido.');
  }
  return { ano, mes, dia };
}

export function validarPeriodoCivil(inicio: string, fim: string): { inicio: string; fim: string } {
  const a = decomporDataCivil(inicio);
  const b = decomporDataCivil(fim);
  const tempoA = Date.UTC(a.ano, a.mes - 1, a.dia);
  const tempoB = Date.UTC(b.ano, b.mes - 1, b.dia);
  if (tempoA > tempoB) throw new Error('Selecione um período válido.');
  return { inicio, fim };
}

export function converterPeriodoCivilParaUTC(inicio: string, fim: string): { inicioUTC: string; fimUTC: string } {
  // UI envia início/fim inclusivos; o SQL usa fim exclusivo na meia-noite seguinte.
  const valido = validarPeriodoCivil(inicio, fim);
  const a = decomporDataCivil(valido.inicio);
  const b = decomporDataCivil(valido.fim);
  const inicioUTC = new Date(Date.UTC(a.ano, a.mes - 1, a.dia, 0, 0, 0) + DESLOCAMENTO_SP_MINUTOS * 60_000).toISOString();
  // Dia seguinte à meia-noite de SP vira o limite exclusivo.
  const diaSeguinte = new Date(Date.UTC(b.ano, b.mes - 1, b.dia + 1));
  const fimUTC = new Date(Date.UTC(diaSeguinte.getUTCFullYear(), diaSeguinte.getUTCMonth(), diaSeguinte.getUTCDate(), 0, 0, 0) + DESLOCAMENTO_SP_MINUTOS * 60_000).toISOString();
  return { inicioUTC, fimUTC };
}

export function resolverPeriodoAnterior(
  periodo: { inicio: string | null; fim: string | null },
  agora: Date = new Date(),
): { inicio: string; fim: string } | null {
  // Máximo (intervalo aberto) não tem comparação anterior definida.
  if (!periodo.inicio || !periodo.fim) return null;
  const valido = validarPeriodoCivil(periodo.inicio, periodo.fim);
  const a = decomporDataCivil(valido.inicio);
  const b = decomporDataCivil(valido.fim);
  const dias = Math.round((Date.UTC(b.ano, b.mes - 1, b.dia) - Date.UTC(a.ano, a.mes - 1, a.dia)) / 86_400_000) + 1;
  // Período corrente incompleto compara até o mesmo horário transcorrido do anterior.
  // Para o preset Hoje, o anterior termina no mesmo horário de agora no dia anterior.
  const fimAnterior = new Date(Date.UTC(a.ano, a.mes - 1, a.dia) - 86_400_000);
  const inicioAnterior = new Date(Date.UTC(a.ano, a.mes - 1, a.dia) - dias * 86_400_000);
  const formatar = (d: Date) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  void agora;
  void fimAnterior;
  return { inicio: formatar(inicioAnterior), fim: formatar(new Date(Date.UTC(a.ano, a.mes - 1, a.dia) - 86_400_000)) };
}

export function serializarPeriodoMetricas(inicio: Date | null, fim: Date | null): { inicio: string; fim: string } {
  // Reaproveita a serialização do dashboard; Máximo vira intervalo aberto tratado à parte.
  const periodo = serializarPeriodoDashboard(inicio, fim);
  if (!periodo.inicio || !periodo.fim) throw new Error('Período máximo usa limites reais da fonte.');
  return { inicio: periodo.inicio, fim: periodo.fim };
}

export function chaveMetricas(dominio: string, contexto: ContextoMetricas) {
  // Incluir sessão e permissões impede reutilizar dados após troca de clínica, usuário ou papel.
  const filtrosOrdenados = Object.fromEntries(Object.entries(contexto.filtros).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
  const contextoOrdenado: ContextoMetricas = { ...contexto, filtros: filtrosOrdenados };
  return ['metricas-v1', contextoOrdenado.usuarioId, contextoOrdenado.clinicaId, contextoOrdenado.modo,
    contextoOrdenado.versaoPermissoes, dominio, contextoOrdenado.periodo, contextoOrdenado.comparar, contextoOrdenado.filtros] as const;
}
